import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import Select from "react-select";

import {CheckboxMarker, DropdownIndicator, filterOption, listSelectStyles, MultiValueLabel, MultiValueRemove, optionWithMarker} from "./listSelectParts";

/**
 * Multiple choice version of RadioListSelect: a checkbox on each option, the chosen
 * values as badges in the control (one clipped line, every badge while the menu is open).
 * The selection is handed over once, when the menu closes or a badge is removed.
 */
CheckboxListSelect.propTypes = {
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        adornment: PropTypes.node,
    })).isRequired,
    value: PropTypes.arrayOf(PropTypes.string),
    placeholder: PropTypes.node,
    onChange: PropTypes.func.isRequired,
    max: PropTypes.number,
    highlighted: PropTypes.bool,
    disabled: PropTypes.bool,
    searchable: PropTypes.bool,
    noOptionsText: PropTypes.node,
};

const COMPONENTS = {Option: optionWithMarker(CheckboxMarker), MultiValueLabel, MultiValueRemove, DropdownIndicator, IndicatorSeparator: null, ClearIndicator: null};

const sameValues = (a, b) => a.length === b.length && a.every((item) => b.includes(item));

function CheckboxListSelect({options, value = [], placeholder, onChange, max = null, highlighted = false, disabled = false, searchable = false, noOptionsText = null}) {
    const [draft, setDraft] = useState(value);
    const [menuOpen, setMenuOpen] = useState(false);
    const styles = useMemo(() => listSelectStyles(highlighted, true), [highlighted]);
    const full = null !== max && draft.length >= max;
    const choices = options.map((option) => ({...option, isDisabled: full && !draft.includes(option.value)}));

    const valueKey = JSON.stringify(value ?? []);
    useEffect(() => {
        setDraft(JSON.parse(valueKey));
    }, [valueKey]);

    const commit = (values) => {
        if (!sameValues(values, value ?? [])) {
            onChange(values);
        }
    };

    return (
        <Select
            isMulti
            options={choices}
            value={choices.filter((option) => draft.includes(option.value))}
            placeholder={placeholder}
            isDisabled={disabled}
            isSearchable={searchable}
            isClearable={false}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            filterOption={filterOption}
            noOptionsMessage={() => noOptionsText}
            components={COMPONENTS}
            styles={styles}
            menuPortalTarget={document.body}
            menuShouldScrollIntoView={false}
            menuIsOpen={menuOpen}
            onMenuOpen={() => setMenuOpen(true)}
            onMenuClose={() => {
                setMenuOpen(false);
                commit(draft);
            }}
            onChange={(selected, {action}) => {
                const values = (selected ?? []).map((option) => option.value);
                setDraft(values);
                if (!menuOpen && 'remove-value' === action) {
                    commit(values);
                }
            }}
        />
    );
}

export default CheckboxListSelect;
